import React, { useState } from 'react';
import AdCarousel from './AdCarousel';
import AdDrawer from './AdDrawer';

/**
 * AdCard — a sponsored feed post for a premium business. Rendered as the 4th
 * item in the home feed. The business may or may not offer a discount; the ad
 * shows either way via AdCard. "Learn more" toggles a details drawer.
 */
export default function AdCard({ ad, analytics }) {
    const [drawerOpen, setDrawerOpen] = useState(false);

    const businessUrl = `/businesses/${ad.businessId}/`;

    // Hero-first carousel when the business has more than one photo; falls back
    // to `imageUrl` for payloads cached before `images` shipped.
    const images = ad.images?.length
        ? ad.images
        : ad.imageUrl
            ? [ad.imageUrl]
            : [];

    const handleClick = (label) => {
        if (analytics) {
            analytics.trackInteraction(label, {
                businessId: ad.businessId,
                businessTitle: ad.title
            });
        }
    };

    const offer = ad.discount
        ? {
            label: 'Member offer',
            text: ad.discount,
            code: ad.offerCode ? `Code ${ad.offerCode}` : null,
            note: ad.offerNote,
        }
        : null;

    return (
        <article>
            {/*
              Instagram-style header: avatar, business name and a "Promoted"
              label — mirrors the event post layout.
            */}
            <div className="post-head">
                {ad.avatarUrl && (
                    <a href={businessUrl} onClick={() => handleClick('click_ad_author')} className="flex-shrink-0 no-underline" aria-label={ad.title}>
                        <div className="story-ring">
                            <img src={ad.avatarUrl} alt={ad.title} className="post-avatar" loading="lazy" />
                        </div>
                    </a>
                )}
                <div className="post-author">
                    <a href={businessUrl} onClick={() => handleClick('click_ad_author')} className="no-underline group">
                        <p className="post-name group-hover:text-brand-700 transition-colors">{ad.title}</p>
                    </a>
                    {ad.handle && (
                        <p className="post-handle">{ad.handle}</p>
                    )}
                </div>
                <span className="post-meta">Promoted</span>
            </div>

            <div className="bg-white border border-gray-200/70 rounded-md overflow-hidden" style={{ boxShadow: '0 1px 2px rgb(0 0 0 / 0.04)' }}>
                {images.length > 1 ? (
                    <AdCarousel
                        images={images}
                        title={ad.title}
                        href={businessUrl}
                        onSlideClick={() => handleClick('click_ad_image')}
                    />
                ) : images.length === 1 ? (
                    <a href={businessUrl} className="block no-underline" onClick={() => handleClick('click_ad_image')}>
                        <img
                            src={images[0]}
                            alt={ad.title}
                            className="w-full h-auto object-contain"
                            loading="lazy"
                        />
                    </a>
                ) : null}

                <div className="p-4 md:p-5">
                    <h3 className="post-title" style={{ fontSize: '1.15rem' }}>
                        <a href={businessUrl} className="hover:text-brand-700 no-underline" onClick={() => handleClick('click_ad_title')}>
                            {ad.title}
                        </a>
                    </h3>

                    {ad.description && (
                        <p className="post-body text-sm line-clamp-3 mb-3">{ad.description}</p>
                    )}

                    {offer && (
                        <div className="bg-brand-50 border border-brand-100 rounded-lg p-3 mb-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-brand-800 mb-0.5">{offer.label}</p>
                            <p className="font-semibold text-gray-900">{offer.text}</p>
                            {offer.code && <p className="text-sm text-brand-800 font-medium">{offer.code}</p>}
                            {offer.note && <p className="text-sm text-gray-600 mt-0.5">{offer.note}</p>}
                        </div>
                    )}

                    <button
                        type="button"
                        className="post-cta"
                        onClick={() => {
                            handleClick('click_ad_cta');
                            setDrawerOpen(true);
                        }}
                    >
                        Learn more
                    </button>
                </div>
            </div>

            {drawerOpen && (
                <AdDrawer
                    ad={ad}
                    analytics={analytics}
                    position="fixed"
                    onClose={() => setDrawerOpen(false)}
                />
            )}
        </article>
    );
}